import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Copy, Download, FileText, Maximize2, Send } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import vscDarkPlus from 'react-syntax-highlighter/dist/esm/styles/prism/vsc-dark-plus';
import { citationIndex, linkCitations, useShownNumber, type AgentSource } from './citations';

const LANGUAGE = /language-([\w+-]+)/;

function codeText(children: React.ReactNode): string {
  return String(React.Children.toArray(children).join('')).replace(/\n$/, '');
}

/** Fence the agent wraps a writer draft in (````aivia-draft <title>); see agentcore/agent/aivia/turn.py. */
export const DRAFT_LANGUAGE = 'aivia-draft';
/** This fence, or the older name earlier releases wrote: answers saved before the rename still use it. */
const DRAFT_FENCE_LANGUAGE = /^(?:ai|a)via-draft$/;

function downloadText(filename: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/markdown;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Wide screens show the draft panel beside the chat instead of over it. */
const SIDE_BY_SIDE = '(min-width: 1024px)';
/** Phones: the bottom tab bar (MobileTabBar, h-14, below md) stays visible, so the panel ends above it. */
const PHONE = '(max-width: 767px)';
/** Short screens (phone landscape, zoomed in): the revise box scrolls with the draft instead of being pinned. */
const SHORT = '(max-height: 480px)';
const TAB_BAR_HEIGHT = '3.5rem';
/** The on-screen keyboard is open when it hides more than this much of the layout viewport. */
const KEYBOARD_MIN_PX = 120;

/**
 * Height of the part of the page the user can actually see. Mobile keyboards shrink the visual
 * viewport but not the layout viewport a fixed panel is sized against, so a phone panel follows
 * this instead; the keyboard then never covers the revise box. null where the API is missing.
 */
function useVisibleHeight(active: boolean): number | null {
  const [height, setHeight] = useState<number | null>(null);
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!active || !vv) return;
    const update = () => setHeight(vv.height + vv.offsetTop);
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [active]);
  return active ? height : null;
}
const PANEL_WIDTH = 'min(40vw, 720px)';
/** Read by the canvas and the composer to make room for the panel (CanvasUI). */
const PANEL_VAR = '--aivia-side-panel';
/** Docked panels open right now; the page keeps room for them until the last one closes. */
let dockedPanels = 0;

function dockPanel(): () => void {
  dockedPanels += 1;
  document.documentElement.style.setProperty(PANEL_VAR, PANEL_WIDTH);
  return () => {
    dockedPanels -= 1;
    if (dockedPanels === 0) document.documentElement.style.removeProperty(PANEL_VAR);
  };
}

/**
 * Sends a change request for a draft, or undefined while AIVIA is answering. Context rather than a
 * prop of the markdown renderer: a renderer that changes makes react-markdown remount the draft,
 * which would close an open panel and lose an unsent revision when an answer starts.
 */
const ReviseContext = createContext<((text: string) => void) | undefined>(undefined);

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const update = () => setMatches(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, [query]);
  return matches;
}

/** `compact`: icon-only below `sm`, so the draft card's title keeps its room on a phone. */
function DraftActions({ title, text, compact = false }: { title: string; text: string; compact?: boolean }) {
  const [copied, setCopied] = useState(false);
  const button = 'inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700';
  const label = compact ? 'hidden sm:inline' : undefined;
  return (
    <>
      <button
        type="button"
        className={button}
        aria-label={copied ? 'Copied' : 'Copy'}
        onClick={() =>
          void navigator.clipboard?.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          })
        }
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}{' '}
        <span className={label}>{copied ? 'Copied' : 'Copy'}</span>
      </button>
      <button
        type="button"
        className={button}
        aria-label="Download"
        onClick={() => downloadText(`${title.replace(/[^\w -]+/g, '').trim() || 'draft'}.md`, text)}
      >
        <Download className="h-3.5 w-3.5" /> <span className={label}>Download</span>
      </button>
    </>
  );
}

/** Ask AIVIA to change the draft without leaving the panel; sends a normal follow-up message. */
function ReviseDraft({ title, onRevise, onSent }: { title: string; onRevise: (text: string) => void; onSent: () => void }) {
  const [value, setValue] = useState('');
  const send = () => {
    const ask = value.trim();
    if (!ask) return;
    onRevise(`Revise the "${title}" draft: ${ask}`);
    setValue('');
    onSent();
  };
  return (
    <form
      className="flex flex-none items-end gap-2 border-t border-orange-100 bg-white px-6 py-3 dark:border-orange-900/40 dark:bg-gray-950"
      onSubmit={(e) => {
        e.preventDefault();
        send();
      }}
    >
      <label htmlFor={`revise-${title}`} className="sr-only">
        Ask AIVIA to change this draft
      </label>
      <textarea
        id={`revise-${title}`}
        rows={2}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            send();
          }
        }}
        placeholder="Ask for changes, e.g. make it shorter"
        className="min-w-0 flex-1 resize-none rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 dark:border-gray-700 dark:bg-gray-900"
      />
      <button
        type="submit"
        disabled={!value.trim()}
        aria-label="Send change request"
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-40"
      >
        <Send className="h-4 w-4" />
      </button>
    </form>
  );
}

/**
 * A writer draft (email, proposal, post) as an artifact: a preview card in the answer that opens
 * in a side panel to read, copy, download or revise. Streams in place while the draft is written.
 * On wide screens the panel sits beside the chat (non-modal), so the conversation and composer
 * stay usable; on smaller screens it covers the page and carries its own revise box.
 */
function DraftArtifact({ title, text, sources }: { title: string; text: string; sources: AgentSource[] }) {
  const [open, setOpen] = useState(false);
  // The panel isn't opened by a Radix trigger, so focus is returned to Open by hand when it closes.
  const openButton = useRef<HTMLButtonElement>(null);
  const onRevise = useContext(ReviseContext);
  const sideBySide = useMediaQuery(SIDE_BY_SIDE);
  const phone = useMediaQuery(PHONE);
  const short = useMediaQuery(SHORT);
  const visibleHeight = useVisibleHeight(open && phone);
  const keyboardOpen = visibleHeight !== null && window.innerHeight - visibleHeight > KEYBOARD_MIN_PX;
  const docked = open && sideBySide;
  useEffect(() => (docked ? dockPanel() : undefined), [docked]);
  return (
    <div className="my-3 overflow-hidden rounded-lg border border-orange-200 bg-white dark:border-orange-900/60 dark:bg-gray-900/40" data-artifact="draft">
      {/* not-prose only on the header: on the card it also removed the preview's paragraph spacing. */}
      <div className="not-prose flex items-center gap-2 border-b border-orange-100 bg-orange-50/60 px-3 py-2 dark:border-orange-900/40 dark:bg-orange-900/20">
        <FileText className="h-4 w-4 shrink-0 text-orange-600 dark:text-orange-400" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 dark:text-gray-100">{title}</span>
        <DraftActions title={title} text={text} compact />
        <button
          ref={openButton}
          type="button"
          className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-orange-700 hover:bg-orange-100 dark:text-orange-300 dark:hover:bg-orange-900/40"
          onClick={() => setOpen(true)}
        >
          <Maximize2 className="h-3.5 w-3.5" /> Open
        </button>
      </div>
      <div className="relative max-h-64 overflow-hidden px-4 py-3">
        <AgentMarkdown content={text} sources={sources} />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white dark:from-gray-900" />
      </div>
      <Sheet open={open} onOpenChange={setOpen} modal={!sideBySide}>
        <SheetContent
          side="right"
          overlay={!sideBySide}
          // Only the draft scrolls: the header (with Close) and the revise box stay in view.
          className="flex w-full flex-col overflow-hidden border-l-2 border-orange-200 p-0 sm:max-w-2xl dark:border-orange-900/60"
          style={
            sideBySide
              ? { width: PANEL_WIDTH, maxWidth: PANEL_WIDTH }
              : phone
                ? keyboardOpen
                  ? // Keyboard open: end at the top of the keyboard (the tab bar is behind it).
                    { top: 0, bottom: 'auto', height: `${visibleHeight}px` }
                  : { bottom: TAB_BAR_HEIGHT, height: 'auto' }
                : undefined
          }
          // Docked beside the chat: typing in the composer or scrolling the thread keeps it open.
          onInteractOutside={sideBySide ? (e) => e.preventDefault() : undefined}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            openButton.current?.focus();
          }}
        >
          <SheetHeader className="flex-none space-y-1 border-b border-orange-100 bg-orange-50 px-6 py-4 pr-14 text-left dark:border-orange-900/40 dark:bg-orange-950">
            <SheetTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4 shrink-0 text-orange-600 dark:text-orange-400" aria-hidden="true" />
              <span className="truncate">{title}</span>
            </SheetTitle>
            <SheetDescription>Draft from AIVIA. Review before sending.</SheetDescription>
            <div className="not-prose -ml-2 flex gap-1 pt-1">
              <DraftActions title={title} text={text} />
            </div>
          </SheetHeader>
          {/* The header (with Close) never scrolls. On short screens the revise box scrolls with the
              draft, so a tall header plus a pinned box can't squeeze it out of reach. */}
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            <AgentMarkdown content={text} sources={sources} />
            {onRevise && short && (
              <div className="-mx-6 -mb-4 mt-4">
                <ReviseDraft title={title} onRevise={onRevise} onSent={() => setOpen(false)} />
              </div>
            )}
          </div>
          {onRevise && !short && <ReviseDraft title={title} onRevise={onRevise} onSent={() => setOpen(false)} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/** An inline [n] citation, numbered as the Sources list shows it. */
function CitationChip({ source, cite }: { source?: AgentSource; cite: number }) {
  const n = useShownNumber()(cite);
  const chip = (
    <sup className="mx-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded bg-orange-100 px-1 text-[10px] font-semibold text-orange-700 no-underline dark:bg-orange-900/40 dark:text-orange-300">
      {n}
    </sup>
  );
  return source?.url ? (
    <a href={source.url} target="_blank" rel="noopener noreferrer" title={source.title} aria-label={`Source ${n}: ${source.title}`} className="no-underline">
      {chip}
    </a>
  ) : (
    chip
  );
}

function buildComponents(sources: AgentSource[]): Components {
  return {
    pre({ children }) {
      const code = React.Children.toArray(children).find(React.isValidElement) as React.ReactElement<any> | undefined;
      const language = LANGUAGE.exec(code?.props?.className ?? '')?.[1];
      if (code && language && DRAFT_FENCE_LANGUAGE.test(language)) {
        const title = String(code.props.node?.data?.meta ?? '').trim() || 'Draft';
        return <DraftArtifact title={title} text={codeText(code.props.children)} sources={sources} />;
      }
      if (code && language) {
        return (
          <SyntaxHighlighter
            language={language}
            style={vscDarkPlus}
            customStyle={{ margin: '0.75rem 0', borderRadius: '0.5rem', fontSize: '0.8125rem' }}
          >
            {codeText(code.props.children)}
          </SyntaxHighlighter>
        );
      }
      return (
        <pre className="not-prose my-3 overflow-x-auto rounded-lg bg-gray-900 p-4 text-sm text-gray-100 [&_code]:bg-transparent [&_code]:p-0 [&_code]:text-inherit">
          {children}
        </pre>
      );
    },
    code({ className, children }) {
      return (
        <code className={className ?? 'rounded bg-gray-100 px-1.5 py-0.5 font-mono text-sm text-gray-800 dark:bg-gray-700 dark:text-gray-200'}>
          {children}
        </code>
      );
    },
    table({ children }) {
      return (
        <div className="my-3 overflow-x-auto">
          <table className="min-w-full text-sm">{children}</table>
        </div>
      );
    },
    a({ href, children }) {
      const cite = citationIndex(href);
      if (cite !== null) return <CitationChip source={sources.find((s) => s.index === cite)} cite={cite} />;
      const external = /^https?:/i.test(href ?? '');
      return (
        <a href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
          {children}
        </a>
      );
    },
  };
}

/**
 * The one markdown renderer for AIVIA answers, used while streaming and once saved, so formatting
 * does not change when the stream ends. GFM (tables, task lists, strikethrough), highlighted code,
 * and [n] citations linked to `sources`. Raw HTML is never rendered.
 */
export function AgentMarkdown({
  content,
  sources = [],
  streaming = false,
  onRevise,
}: {
  content: string;
  sources?: AgentSource[];
  streaming?: boolean;
  /** Sends a message asking AIVIA to change a draft; omit while AIVIA is answering. */
  onRevise?: (text: string) => void;
}) {
  const components = useMemo(() => buildComponents(sources), [sources]);
  const markdown = useMemo(() => linkCitations(content, new Set(sources.map((s) => s.index))), [content, sources]);
  return (
    <ReviseContext.Provider value={onRevise}>
      <div className="aivia-markdown prose max-w-none break-words text-foreground dark:prose-invert" data-streaming={streaming || undefined}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {markdown}
        </ReactMarkdown>
        {streaming && <span className="ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-orange-500 align-middle" aria-hidden="true" />}
      </div>
    </ReviseContext.Provider>
  );
}
